import {
  SOL_MARKET_TOKEN_ID,
  formatTokenUnits,
  getDefaultMarketPaymentTokenConfig
} from "../../shared/marketTokens.js";

export function marketPaymentTokenForDisplay(marketOrToken = {}) {
  const fallback = getDefaultMarketPaymentTokenConfig();
  const candidate = marketOrToken?.paymentToken && typeof marketOrToken.paymentToken === "object"
    ? marketOrToken.paymentToken
    : marketOrToken;

  if (!candidate || typeof candidate !== "object") return fallback;
  const hasExplicitTokenShape =
    Boolean(marketOrToken?.paymentToken) ||
    hasTokenValue(candidate.id) ||
    hasTokenValue(candidate.kind) ||
    hasTokenValue(candidate.symbol) ||
    hasTokenValue(candidate.mint) ||
    hasTokenValue(candidate.tokenProgramId) ||
    hasTokenValue(candidate.paymentTokenId) ||
    hasTokenValue(candidate.paymentTokenSymbol) ||
    hasTokenValue(candidate.payment_token_id) ||
    hasTokenValue(candidate.payment_token_symbol) ||
    Array.isArray(candidate.voteAmountUnits) ||
    Array.isArray(candidate.paymentTokenVoteAmountUnits) ||
    Array.isArray(candidate.payment_token_vote_amount_units);
  if (!hasExplicitTokenShape) return fallback;

  const tokenId = resolveTokenId(candidate, marketOrToken, fallback.id);
  const symbol = safeTokenSymbol(
    candidate.symbol ||
    candidate.paymentTokenSymbol ||
    candidate.payment_token_symbol ||
    marketOrToken?.paymentTokenSymbol ||
    fallback.symbol
  );
  return {
    ...fallback,
    ...candidate,
    id: tokenId,
    kind: safeText(candidate.kind || candidate.paymentTokenKind || candidate.payment_token_kind || marketOrToken?.paymentTokenKind || fallback.kind) || fallback.kind,
    symbol,
    name: safeText(candidate.name || symbol || fallback.name) || fallback.name,
    decimals: safeInteger(candidate.decimals ?? candidate.paymentTokenDecimals ?? candidate.payment_token_decimals ?? marketOrToken?.paymentTokenDecimals, fallback.decimals),
    voteAmountUnits: safeArray(candidate.voteAmountUnits ?? candidate.paymentTokenVoteAmountUnits ?? candidate.payment_token_vote_amount_units ?? marketOrToken?.paymentTokenVoteAmountUnits ?? fallback.voteAmountUnits),
    voteAmountLabels: safeArray(candidate.voteAmountLabels ?? candidate.paymentTokenVoteAmountLabels ?? candidate.payment_token_vote_amount_labels ?? marketOrToken?.paymentTokenVoteAmountLabels ?? fallback.voteAmountLabels)
  };
}

export function marketVoteAmountOptionsForDisplay(market = {}) {
  const token = marketPaymentTokenForDisplay(market);
  const explicitOptions = Array.isArray(market?.voteAmountOptions) ? market.voteAmountOptions : [];
  const options = explicitOptions.length
    ? explicitOptions
    : token.voteAmountUnits.map((units, index) => ({
        units,
        totalUnits: units,
        label: token.voteAmountLabels[index] || formatTokenUnits(units, token.decimals),
        uiAmount: formatTokenUnits(units, token.decimals),
        symbol: token.symbol
      }));

  return options
    .map((option, index) => normalizeVoteAmountOption(option, token, index))
    .filter((option) => option.key && option.voteAmount !== "");
}

export function formatMarketTokenAmount(value, marketOrToken = {}, { units } = {}) {
  const token = marketPaymentTokenForDisplay(marketOrToken);
  const symbol = safeTokenSymbol(token.symbol);
  const unitsValue = units ?? amountUnitsFrom(value);
  if (unitsValue !== null && unitsValue !== undefined && String(unitsValue).trim() !== "") {
    try {
      return `${formatTokenUnits(unitsValue, token.decimals)} ${symbol}`;
    } catch {
      // Fall through to display-only numeric formatting.
    }
  }

  const amount = Number(value ?? 0);
  const safeAmount = Number.isFinite(amount) && amount > 0 ? amount : 0;
  return `${safeAmount.toFixed(2)} ${symbol}`;
}

export function formatMarketTotalVotes(market = {}) {
  return `${formatMarketTokenAmount(market?.totalVotes ?? market?.total_votes, market)} total votes`;
}

export function formatMarketOptionVotes(option = {}, market = {}) {
  const units =
    option.totalVoteUnits ??
    option.total_vote_units ??
    option.voteUnits ??
    option.vote_units ??
    option.totalUnits ??
    option.total_units ??
    null;
  return `${formatMarketTokenAmount(option.votes ?? option.totalVotes ?? option.total_votes, market, { units })} votes`;
}

export function formatMarketVoteAmountOptionLabel(option = {}, marketOrToken = {}) {
  const token = marketPaymentTokenForDisplay(marketOrToken);
  const label = safeText(option.label || option.uiAmount || option.value || "");
  return `${label || "0"} ${safeTokenSymbol(option.symbol || token.symbol)}`;
}

function normalizeVoteAmountOption(option = {}, token, index) {
  const units = safeText(option.units ?? option.totalUnits ?? option.total_units ?? "");
  const uiAmount = safeText(option.uiAmount ?? option.ui_amount ?? "");
  const label = safeText(option.label || uiAmount || (units ? formatTokenUnits(units, token.decimals) : ""));
  const voteAmount = uiAmount || label;
  return {
    ...option,
    key: units || `${voteAmount}:${index}`,
    units,
    totalUnits: safeText(option.totalUnits ?? option.total_units ?? units),
    label,
    uiAmount,
    voteAmount,
    symbol: safeTokenSymbol(option.symbol || token.symbol)
  };
}

function amountUnitsFrom(value) {
  if (!value || typeof value !== "object") return null;
  return value.units ?? value.totalUnits ?? value.total_units ?? value.amountUnits ?? value.amount_units ?? null;
}

function safeText(value) {
  return String(value ?? "").trim();
}

function safeTokenSymbol(value) {
  return safeText(value || "SOL").toUpperCase() || "SOL";
}

function safeInteger(value, fallback) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric >= 0 ? numeric : fallback;
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function hasTokenValue(value) {
  return String(value ?? "").trim() !== "";
}

function resolveTokenId(candidate, marketOrToken, fallbackId) {
  const explicitMarketTokenId = safeText(
    candidate.paymentTokenId ||
    candidate.payment_token_id ||
    marketOrToken?.paymentTokenId ||
    marketOrToken?.payment_token_id
  );
  if (explicitMarketTokenId) return explicitMarketTokenId;
  if (marketOrToken?.paymentToken || hasTokenValue(candidate.kind) || hasTokenValue(candidate.symbol)) {
    return safeText(candidate.id || fallbackId) || SOL_MARKET_TOKEN_ID;
  }
  return fallbackId || SOL_MARKET_TOKEN_ID;
}
